import type { TourPopoverSide } from '../types/learnerTour';

export interface PopoverPlacement {
  side: 'top' | 'bottom' | 'left' | 'right';
  top: number;
  left: number;
  arrowOffset: number;
}

const POPOVER_MARGIN = 12;
const VIEWPORT_PAD = 8;

export function computePopoverPlacement(
  targetRect: DOMRect,
  popoverWidth: number,
  popoverHeight: number,
  preferred: TourPopoverSide,
  isRtl: boolean
): PopoverPlacement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaces = {
    top: targetRect.top - POPOVER_MARGIN,
    bottom: vh - targetRect.bottom - POPOVER_MARGIN,
    left: targetRect.left - POPOVER_MARGIN,
    right: vw - targetRect.right - POPOVER_MARGIN,
  };

  const sides: Array<'top' | 'bottom' | 'left' | 'right'> =
    preferred === 'auto'
      ? ['bottom', 'top', 'right', 'left']
      : [preferred, 'bottom', 'top', 'right', 'left'].filter(
          (s, i, arr) => arr.indexOf(s) === i
        ) as Array<'top' | 'bottom' | 'left' | 'right'>;

  let chosen: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
  for (const side of sides) {
    const fitsVert = side === 'left' || side === 'right'
      ? spaces[side] >= popoverWidth
      : spaces[side] >= popoverHeight;
    if (fitsVert) {
      chosen = side;
      break;
    }
  }

  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  let top = 0;
  let left = 0;
  let arrowOffset = 0;

  if (chosen === 'bottom') {
    top = targetRect.bottom + POPOVER_MARGIN;
    left = targetCenterX - popoverWidth / 2;
    arrowOffset = targetCenterX - left;
  } else if (chosen === 'top') {
    top = targetRect.top - popoverHeight - POPOVER_MARGIN;
    left = targetCenterX - popoverWidth / 2;
    arrowOffset = targetCenterX - left;
  } else if (chosen === 'right') {
    left = isRtl ? targetRect.left - popoverWidth - POPOVER_MARGIN : targetRect.right + POPOVER_MARGIN;
    top = targetCenterY - popoverHeight / 2;
    arrowOffset = targetCenterY - top;
  } else {
    left = isRtl ? targetRect.right + POPOVER_MARGIN : targetRect.left - popoverWidth - POPOVER_MARGIN;
    top = targetCenterY - popoverHeight / 2;
    arrowOffset = targetCenterY - top;
  }

  left = Math.max(VIEWPORT_PAD, Math.min(left, vw - popoverWidth - VIEWPORT_PAD));
  top = Math.max(VIEWPORT_PAD, Math.min(top, vh - popoverHeight - VIEWPORT_PAD));

  if (chosen === 'top' || chosen === 'bottom') {
    arrowOffset = targetCenterX - left;
    arrowOffset = Math.max(20, Math.min(arrowOffset, popoverWidth - 20));
  } else {
    arrowOffset = targetCenterY - top;
    arrowOffset = Math.max(20, Math.min(arrowOffset, popoverHeight - 20));
  }

  return { side: chosen, top, left, arrowOffset };
}

export function scrollTargetIntoViewIfNeeded(el: HTMLElement, behavior: ScrollBehavior = 'smooth'): void {
  // Fixed header controls are already on screen. Scrolling them recenters the page and shifts the bar.
  if (el.closest('nav')) return;
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const pad = 80;
  if (rect.top >= pad && rect.bottom <= vh - pad) return;
  el.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
}

function findVerticalScrollParent(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/** Scroll inside a modal/overlay scroller without moving the page behind it. */
export function scrollOverlayTargetIntoView(el: HTMLElement): void {
  const panel = el.closest('[role="tabpanel"]');
  if (panel instanceof HTMLElement) {
    const elRect = el.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const pad = 24;
    if (elRect.top < panelRect.top + pad || elRect.bottom > panelRect.bottom - pad) {
      panel.scrollTop += elRect.top - panelRect.top - panelRect.height / 2 + elRect.height / 2;
    }
    return;
  }
  const scroller = findVerticalScrollParent(el);
  if (!scroller) return;
  const elRect = el.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const pad = 48;
  if (elRect.top >= scrollerRect.top + pad && elRect.bottom <= scrollerRect.bottom - pad) return;
  scroller.scrollTop += elRect.top - scrollerRect.top - scrollerRect.height / 2 + elRect.height / 2;
}

/** Clear any scroll lock styles that may persist after overlays / tours. */
export function releaseDocumentScrollLock(): void {
  const { body } = document;
  const html = document.documentElement;
  const wasFixed = body.style.position === 'fixed';
  const savedY = wasFixed ? Math.abs(parseInt(body.style.top || '0', 10)) : null;
  body.style.overflow = '';
  body.style.position = '';
  body.style.top = '';
  body.style.width = '';
  html.style.overflow = '';
  html.style.overscrollBehavior = '';
  if (wasFixed && savedY != null && Number.isFinite(savedY)) {
    window.scrollTo(0, savedY);
  }
}

/** Reset window scroll after layout shifts (e.g. collapsible panels closing). */
export function resetWindowScrollTop(behavior: ScrollBehavior = 'auto'): void {
  window.scrollTo({ top: 0, left: 0, behavior });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/** Scroll a wired tour target to the top of the viewport (e.g. library header after tour). */
export function scrollTourTargetToPageTop(targetKey: string, behavior: ScrollBehavior = 'auto'): void {
  const escaped =
    typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(targetKey) : targetKey.replace(/"/g, '\\"');
  const el = document.querySelector(`[data-tour-id="${escaped}"]`);
  if (el instanceof HTMLElement) {
    el.scrollIntoView({ behavior, block: 'start', inline: 'nearest' });
    return;
  }
  resetWindowScrollTop(behavior);
}

/** Overlay tours (modals) — never scroll/reset the page behind the modal. */
const OVERLAY_TOUR_PAGE_KEYS = new Set(['quick-settings', 'quick-settings-simple', 'search', 'leitner']);

export function isLearnerTourOverlayPageKey(pageKey: string | null | undefined): boolean {
  if (!pageKey) return false;
  if (OVERLAY_TOUR_PAGE_KEYS.has(pageKey)) return true;
  return pageKey.startsWith('quick-settings-');
}

/** Restore learner page scroll after a tour ends (handles layout collapse). */
export function restoreLearnerPageScrollAfterTour(pageKey: string): void {
  releaseDocumentScrollLock();
  if (isLearnerTourOverlayPageKey(pageKey)) return;
  if (pageKey === 'library') {
    scrollTourTargetToPageTop('library-header', 'auto');
  }
  resetWindowScrollTop('auto');
}
